import React from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import { Sparkles } from 'lucide-react';
import { getImageUrl } from '../../lib/utils';
import 'swiper/css';
import 'swiper/css/navigation';

const BrandsSection = ({ brands, isLoading }) => {
  if (isLoading) {
    return (
      <section className="bg-white py-6 md:py-8">
        <div className="w-full md:container mx-auto px-2 sm:px-4">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-900 flex items-center gap-2">
              Top Brands <Sparkles className="w-5 h-5 text-red-500" />
            </h2>
          </div>
          <div className="flex gap-4 overflow-hidden">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="flex-none w-24 sm:w-32 md:w-36 flex flex-col items-center animate-pulse">
                <div className="w-full aspect-square bg-gray-200 rounded-full mb-2"></div>
                <div className="w-2/3 h-3 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!brands || brands.length === 0) return null;

  // Show up to 10 brands, rest are reachable via "View All"
  const displayBrands = brands.slice(0, 10);

  return (
    <section className="bg-white py-6 md:py-8">
      <div className="w-full md:container mx-auto px-2 sm:px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-[#111827] flex items-center gap-2">
            Top Brands <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-red-500" strokeWidth={2} />
          </h2>
          <Link
            to="/brands"
            className="text-sm md:text-base font-bold text-[#1E3A8A] hover:text-blue-800 flex items-center transition-colors group"
          >
            View All <span className="ml-1 transition-transform group-hover:translate-x-1">›</span>
          </Link>
        </div>

        {/* Brands Carousel */}
        <div className="relative brands-section-slider">
          <Swiper
            modules={[Navigation, Autoplay]}
            spaceBetween={10}
            slidesPerView={4}
            navigation
            autoplay={{ delay: 4000, disableOnInteraction: false }}
            breakpoints={{
              480: { slidesPerView: 5, spaceBetween: 10 },
              640: { slidesPerView: 6, spaceBetween: 12 },
              768: { slidesPerView: 7, spaceBetween: 12 },
              1024: { slidesPerView: 9, spaceBetween: 16 },
              1280: { slidesPerView: 10, spaceBetween: 16 },
            }}
            className="px-2 py-1"
          >
            {displayBrands.map((brand) => (
              <SwiperSlide key={brand.id || brand._id}>
                <Link
                  to={`/brands/${brand.slug}`}
                  className="flex flex-col items-center group w-full"
                >
                  <div className="w-[80%] max-w-[88px] mx-auto aspect-square border border-slate-200 rounded-md flex items-center justify-center bg-white transition-shadow duration-300 hover:border-blue-200 hover:shadow-md overflow-hidden transform-gpu">
                    {brand.picture || brand.logo ? (
                      <img
                        src={getImageUrl(brand.picture || brand.logo)}
                        alt={brand.name}
                        className="w-full h-full object-contain p-1.5 transition-transform duration-300 group-hover:scale-110 transform-gpu will-change-transform"
                        loading="lazy"
                      />
                    ) : (
                      <span className="text-2xl font-bold text-gray-300">
                        {brand.name?.charAt(0)}
                      </span>
                    )}
                  </div>
                  <span className="mt-2.5 text-[11px] sm:text-xs md:text-sm font-medium text-[#1E3A8A] transition-colors duration-300 group-hover:text-blue-900 truncate w-full text-center">
                    {brand.name}
                  </span>
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      {/* Add some custom styles for the swiper navigation buttons specifically for brands */}
      <style dangerouslySetInnerHTML={{
        __html: `
        .brands-section-slider .swiper-button-next,
        .brands-section-slider .swiper-button-prev {
          background-color: white;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          color: #1E3A8A;
          border: 1px solid #e5e7eb;
        }
        .brands-section-slider .swiper-button-next:after,
        .brands-section-slider .swiper-button-prev:after {
          font-size: 14px;
          font-weight: bold;
        }
        .brands-section-slider .swiper-button-disabled {
          opacity: 0;
        }
      `}} />
    </section>
  );
};

export default BrandsSection;
